import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { extractVideoId, isValidYoutubeId } from '../utils/extractVideoId';
import { sendToWebhook } from '../utils/webhookService';
import { Mode } from '../types';
import { LinkIcon, SendIcon, AlertCircleIcon, CheckCircleIcon, LoaderIcon } from 'lucide-react';

interface URLFormProps {
  mode: Mode;
  onVideoIdExtracted: (videoId: string | null) => void;
}

type SubmissionState = 'idle' | 'loading' | 'success' | 'error';

const URLForm: React.FC<URLFormProps> = ({ mode, onVideoIdExtracted }) => {
  const [url, setUrl] = useState('');
  const [videoId, setVideoId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [submissionMessage, setSubmissionMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const extractedId = extractVideoId(url);
    setVideoId(extractedId);
    onVideoIdExtracted(extractedId);
    
    if (url && !extractedId) {
      setError('Invalid YouTube URL. Please check the format.');
    } else if (extractedId && !isValidYoutubeId(extractedId)) {
      setError('The extracted video ID appears invalid.');
    } else {
      setError(null);
    }
  }, [url, onVideoIdExtracted]);

  useEffect(() => {
    if (submissionState === 'success' || submissionState === 'error') {
      const timer = setTimeout(() => {
        setSubmissionState('idle');
        setSubmissionMessage('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [submissionState]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!videoId || !isValidYoutubeId(videoId)) {
      setError('Please enter a valid YouTube URL.');
      return;
    }
    
    setSubmissionState('loading');
    
    try {
      const response = await sendToWebhook(videoId, mode);
      
      if (response.ok) {
        setSubmissionState('success');
        setSubmissionMessage(`Video ID successfully sent to ${mode} webhook!`);
        setUrl('');
        inputRef.current?.focus();
      } else {
        setSubmissionState('error');
        setSubmissionMessage(`Error: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      setSubmissionState('error');
      setSubmissionMessage('Error connecting to webhook. Please try again.');
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto">
      <form onSubmit={handleSubmit} className="mt-6">
        <div className="relative">
          <label htmlFor="youtube-url" className="block text-sm font-medium text-gray-300 mb-1">
            YouTube URL
          </label>
          <div className="flex rounded-md shadow-sm">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-700 bg-gray-800 text-gray-400">
              <LinkIcon size={18} />
            </span>
            <input
              ref={inputRef}
              type="text"
              id="youtube-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste YouTube URL here..."
              className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-700 bg-gray-800 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
              aria-invalid={!!error}
              aria-describedby={error ? "url-error" : undefined}
            />
          </div>
          
          {error && (
            <div id="url-error" className="mt-1 flex items-center text-sm text-red-400">
              <AlertCircleIcon size={16} className="mr-1" />
              {error}
            </div>
          )}
          
          {videoId && !error && (
            <div className="mt-1 flex items-center text-sm text-green-400">
              <CheckCircleIcon size={16} className="mr-1" />
              Video ID extracted: <code className="ml-1 bg-gray-800 px-1 rounded">{videoId}</code>
            </div>
          )}
        </div>
        
        <div className="mt-4 flex justify-between items-center">
          <div className="text-sm font-medium text-gray-300">
            Mode: <span className={`${mode === 'prod' ? 'text-red-400' : 'text-blue-400'}`}>
              {mode === 'prod' ? 'Production' : 'Test'}
            </span>
          </div>
          
          <button
            type="submit"
            disabled={!videoId || !!error || submissionState === 'loading'}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white 
              ${submissionState === 'loading' || !videoId || !!error
                ? 'bg-gray-700 cursor-not-allowed'
                : 'rainbow-gradient hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              } transition-all duration-200`}
          >
            {submissionState === 'loading' ? (
              <>
                <LoaderIcon size={18} className="mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <SendIcon size={18} className="mr-2" />
                Send to Webhook
              </>
            )}
          </button>
        </div>
        
        {submissionState !== 'idle' && (
          <div className={`mt-4 p-3 rounded-md ${
            submissionState === 'success' ? 'bg-green-900/50 text-green-400' : 
            submissionState === 'error' ? 'bg-red-900/50 text-red-400' : 'bg-gray-800'
          } transition-all duration-300 ease-in`}>
            {submissionState === 'success' && <CheckCircleIcon size={18} className="inline mr-2" />}
            {submissionState === 'error' && <AlertCircleIcon size={18} className="inline mr-2" />}
            {submissionMessage}
          </div>
        )}
      </form>
    </div>
  );
};

export default URLForm;